import { UAParser } from 'ua-parser-js';
import { getUserAgent } from '../config/helpers';

export type DesktopAppPlatform = 'mac-arm' | 'mac-x64' | 'windows' | null;

/**
 * Detects the user's desktop platform for selecting the correct app download URL.
 *
 * Detection strategy:
 * 1. Uses `navigator.userAgentData.getHighEntropyValues` (Chromium browsers) for
 *    reliable architecture detection.
 * 2. Falls back to UA string parsing via UAParser.
 * 3. Defaults Mac to ARM since Apple Silicon is the majority of new Macs
 *
 * @returns The detected platform, or `null` if the platform is not Mac or Windows.
 */
export const getDesktopAppPlatform = async (): Promise<DesktopAppPlatform> => {
  const userAgent = getUserAgent();
  if (!userAgent) {
    return null;
  }

  const parser = new UAParser(userAgent);
  const os = parser.getOS();
  const osName = os.name ?? '';

  const isMac = osName === 'Mac OS';
  const isWindows = osName === 'Windows';

  if (!isMac && !isWindows) {
    return null;
  }

  if (isWindows) {
    return 'windows';
  }

  // Attempt high-entropy UA-CH detection (available in Chromium-based browsers)...
  try {
    const nav = globalThis.navigator as Navigator & {
      userAgentData?: {
        getHighEntropyValues: (
          hints: string[]
        ) => Promise<{ architecture?: string }>;
      };
    };

    if (nav?.userAgentData?.getHighEntropyValues) {
      const { architecture } = await nav.userAgentData.getHighEntropyValues([
        'architecture',
      ]);

      if (architecture === 'arm') {
        return 'mac-arm';
      }

      if (architecture === 'x86') {
        return 'mac-x64';
      }
    }
  } catch {
    // Permission denied or API unavailable — fall through to heuristics
  }

  // ...otherwise, check the CPU architecture from the UA string
  const cpuArch = parser.getCPU().architecture ?? '';

  if (/aarch64|arm/i.test(cpuArch)) {
    return 'mac-arm';
  }

  if (/amd64|x86_64|x64|ia32|x86/i.test(cpuArch)) {
    return 'mac-x64';
  }

  return 'mac-arm';
};
